import { useState } from 'react'
import { castVote, withdrawVote } from '../../lib/wiki'
import { useHubLang } from '../../lib/i18n'
import { WIKI } from '../../lib/hubCopy'

/**
 * One upvote control, used on questions, answers and replies.
 *
 * ── THERE IS NO DOWNVOTE, AND THAT IS A DECISION ────────────────────────────
 *
 * `content_vote.value` is a smallint constrained to 1, so allowing -1 later is
 * a one-line constraint change (the ppe_check_enabled shape). It is 1-only
 * because downvoting is a different social instrument: this is a rebrand where
 * people are asking whether they keep their jobs, and "Do I keep my truck?" at
 * minus six is a message to the person who asked it, sent anonymously by their
 * colleagues.
 *
 * ── THE COUNT COMES BACK FROM THE SERVER, NOT FROM ARITHMETIC ───────────────
 *
 * cast_vote and withdraw_vote both RETURN the new count, and that is what is
 * rendered. Incrementing locally would be one line shorter and would drift the
 * moment two people vote at once — and because voting is idempotent server-side
 * (a double tap on one bar of signal is the same vote arriving twice), local
 * arithmetic would also count a retry as a second vote.
 *
 * ── IT IS SMALL, AND `sm` IS A DENSITY — NOT A ZERO RULE ────────────────────
 *
 * David, 12 Sep: "make the upvote control way smaller everywhere." It was a
 * bordered 3rem tile in its own gutter; it is an inline caret and a number in
 * the actions row.
 *
 * ── THE ZERO IS ALWAYS SHOWN, WHICH REVERSES WHAT THIS FILE SAID (12 Sep) ───
 *
 * The header used to argue that `sm` DROPS A ZERO, citing 018's "absent, not
 * zero" — a reply nobody had voted on showed a bare caret. David: "always
 * starts at 0", and he is right, because 018's rule was misapplied here.
 *
 * 018 is about REACTION kinds: four glyphs are offered, nobody picked 🎉, and
 * printing "0" beside it claims the company looked at that option and declined
 * — so the key is absent and the glyph does not render at all.
 *
 * A vote is not one of four offers. Every item has exactly one vote count, it
 * is always defined, and the database always returns it. Zero is the measured
 * value, not a missing one, so hiding it states nothing truer and costs two
 * real things: the control loses half its width and most of its meaning at
 * rest, and a reader cannot tell "nobody has voted" from "voting is not
 * available here". A number that starts at 0 and becomes 1 when you press it
 * is also the plainest possible confirmation that the press landed.
 *
 * So `sm` now means only what its name says: tighter padding and a smaller
 * caret. If a future surface genuinely wants the count suppressed, that is a
 * prop about the count, not a size.
 *
 * ── AND IT LOOKS PRESSABLE AT REST ──────────────────────────────────────────
 *
 * The 12 Sep version drew no border until hover, which on a phone — where
 * there is no hover — meant the control never announced itself at all. The
 * resting state now carries the hairline and the surface; see the .vote block
 * in hub.css. Voted stays a FILL rather than a colour change, because a
 * colour-only difference on a cheap tablet in daylight is no difference.
 */
export function VoteButton({
  votes,
  iVoted,
  target,
  onChange,
  size = 'md',
}: {
  votes: number
  iVoted: boolean
  target: { contentId?: string; commentId?: string }
  onChange: (votes: number, iVoted: boolean) => void
  /** 'sm' inside a thread: tighter padding and a smaller caret. Nothing else. */
  size?: 'md' | 'sm'
}) {
  const w = WIKI[useHubLang()]
  const [busy, setBusy] = useState(false)

  async function toggle() {
    setBusy(true)
    try {
      const next = iVoted ? await withdrawVote(target) : await castVote(target)
      onChange(next, !iVoted)
    } catch {
      // Deliberately quiet. A failed vote is not worth an error banner over the
      // thing somebody was reading; the count simply does not move, which is
      // the honest rendering of "it did not happen".
    } finally {
      setBusy(false)
    }
  }

  return (
    <button
      type="button"
      className={`vote${iVoted ? ' vote--on' : ''}${size === 'sm' ? ' vote--sm' : ''}`}
      disabled={busy}
      onClick={() => void toggle()}
      aria-pressed={iVoted}
      /* The count is in the LABEL as well as in the glyph, because "▲ 3" read
         aloud is a triangle and a number. */
      aria-label={w.voteLabel(iVoted, votes)}
    >
      <span className="vote__caret" aria-hidden="true">
        ▲
      </span>
      {/* Caret first: the arrow is what says "upvote", and leading with the
          number made a row of replies look like a numbered list.

          ALWAYS RENDERED, ZERO INCLUDED. See the header — a vote count is a
          measured value that is always defined, not one of four optional
          reaction kinds, so 018's absent-not-zero rule does not reach it. */}
      <span className="vote__count">{votes}</span>
    </button>
  )
}
